import { useCallback, useState } from 'react'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { Helmet } from 'react-helmet-async'
import clsx from 'clsx'
import styled from 'styled-components'
import AssistChip from 'LuxKit/Chips/AssistChip'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading, { Heading1Style } from 'LuxKit/Typography/Heading'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import Group from 'components/utils/Group'
import FormatCurrency from 'components/Common/FormatCurrency'
import Image from 'components/Common/Image'
import LayoutContainer from 'components/Common/LayoutContainer'
import Sheet from 'components/utils/Sheet'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useAppSelector } from 'hooks/reduxHooks'
import { getRegionSaveAmount } from 'luxPlus/lib/getRegionSaveAmountCopy'
import { scrollToId } from 'lib/window/scrollUtils'
import useLuxPlusMonthTakeoverEnabledVerticals from '../hooks/useLuxPlusMonthTakeoverEnabledVerticals'
import {
  LUX_PLUS_MONTH_POOL_IMAGE_URL,
  LUX_PLUS_MONTH_BLOB_SHAPE_LARGE_RADIUS_PERCENT,
  LUX_PLUS_MONTH_BLOB_SHAPE_SMALL_RADIUS_PERCENT,
} from '../constants/luxPlusMonthTakeoverConstants'

const HeroContainer = styled(LayoutContainer)`
  padding-top: 0;
  padding-inline: 0;

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(40)};
    padding-inline: ${rem(16)};
  }
`

const Hero = styled(Group)`
  overflow: hidden;

  ${mediaQueryUp.desktop} {
    border-radius: ${rem(16)};
  }
`

const HeroPanel = styled(Sheet)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 0 0;
`

const HeroHeading = styled(Heading)`
  max-width: ${rem(550)};

  ${mediaQueryUp.desktop} {
    ${Heading1Style}
  }
`

const HeroImagePanel = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: ${rem(200)};
  /* Shown above the text/chips panel on mobile, regardless of DOM order */
  order: -1;

  ${mediaQueryUp.desktop} {
    order: 0;
    width: auto;
    height: ${rem(300)};
    flex: 0 0 ${rem(510)};
    align-self: flex-end;
  }
`

const HeroImageFrame = styled.div`
  position: absolute;
  inset: 0;
  /* Size only specifies height — width is auto, which the CSS spec always scales to preserve the
     photo's own intrinsic aspect ratio, so it can never stretch/distort regardless of frame shape
     (same technique as LuxPlusMonthTakeoverHomeBanner's background-size: auto <height>%). */
  background: url(${LUX_PLUS_MONTH_POOL_IMAGE_URL}) lightgray 53% 45% / auto 360% no-repeat;
`

const HeroImageRing = styled.div`
  position: absolute;
  /* Centered horizontally on mobile — the panel spans the full column width there, unlike desktop's
     fixed 510px reference frame, so a fixed inset-inline-start wouldn't stay centered. */
  inset-inline-start: 50%;
  transform: translateX(-50%);
  top: ${rem(-130)};
  width: ${rem(297)};
  height: ${rem(297)};
  border: ${rem(3.9395)} solid ${props => props.theme.palette.neutral.surface.staticWhite};
  /* Base shape is rotated 180deg in the Figma source, so corners are swapped diagonally from the design's raw values */
  border-start-start-radius: ${LUX_PLUS_MONTH_BLOB_SHAPE_LARGE_RADIUS_PERCENT};
  border-start-end-radius: ${LUX_PLUS_MONTH_BLOB_SHAPE_LARGE_RADIUS_PERCENT};
  border-end-end-radius: ${LUX_PLUS_MONTH_BLOB_SHAPE_SMALL_RADIUS_PERCENT};
  border-end-start-radius: ${LUX_PLUS_MONTH_BLOB_SHAPE_LARGE_RADIUS_PERCENT};
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s;

  &.visible {
    opacity: 1;
  }

  &:dir(rtl) {
    transform: translateX(50%);
  }

  ${mediaQueryUp.desktop} {
    inset-inline-start: ${rem(50.1044)};
    transform: none;
    top: ${rem(-144)};
    width: ${rem(404.421)};
    height: ${rem(404.421)};

    &:dir(rtl) {
      transform: none;
    }
  }
`

// Not rendered — only used to detect when the background image has finished loading. Reuses the
// shared Image component rather than a plain <img>, since it already handles the case where the
// image is loaded/cached before the onLoad listener attaches (e.g. shares its URL with
// HeroImageFrame's background, or loads during SSR before hydration).
const ImageLoadDetector = styled(Image)`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
`

const SavingsBadge = styled.div`
  display: flex;
  position: absolute;
  z-index: 1;
  bottom: ${rem(13)};
  inset-inline-end: ${rem(11)};
  width: ${rem(117)};
  height: ${rem(117)};
  border-radius: 50%;
  background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  color: ${props => props.theme.palette.highlight.primary.normalContrast};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(4)};
  text-align: center;
  padding: ${rem(8)};

  ${mediaQueryUp.tablet} {
    top: ${rem(20)};
    bottom: auto;
    inset-inline-end: ${rem(20)};
    width: ${rem(140)};
    height: ${rem(140)};
  }
`

const SavingsAmountRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(2)};
`

const SavingsAmountPrefix = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.1;
`

const SavingsOverText = styled(BodyText)`
  font-size: 9px;
`

const SavingsAmount = styled(Heading)`
  && {
    font-size: 32px;
    line-height: 1;
    font-variant-numeric: lining-nums;

    &.long-amount {
      font-size: 20px;
    }
  }
`

const HeroTextContent = styled.div`
  text-align: center;

  ${mediaQueryUp.desktop} {
    text-align: start;
  }
`

function LuxPlusMonthTakeoverHero() {
  const renderItalic = useCallback((chunks: ReactNode) => <i>{chunks}</i>, [])
  const [heroImageLoaded, setHeroImageLoaded] = useState(false)
  const onHeroImageLoad = useCallback(() => setHeroImageLoaded(true), [])
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const saveAmount = getRegionSaveAmount(regionCode)
  const isLongSaveAmount = saveAmount > 99999
  const enabledVerticals = useLuxPlusMonthTakeoverEnabledVerticals()
  const scrollToHotels = useCallback(() => scrollToId('lux-plus-month-hotels'), [])
  const scrollToTours = useCallback(() => scrollToId('tours'), [])
  const scrollToFlights = useCallback(() => scrollToId('lux-plus-landing-flights-banner'), [])
  const scrollToExperiences = useCallback(() => scrollToId('lux-plus-month-experiences'), [])

  return (
    <HeroContainer variant="large">
      <Helmet>
        <link rel="preload" as="image" href={LUX_PLUS_MONTH_POOL_IMAGE_URL} />
      </Helmet>
      <Hero direction="vertical" desktopDirection="horizontal">
        <HeroPanel background="lux-plus" paletteType="inverse" padding={20} desktopPadding={40}>
          <HeroTextContent>
            <Group direction="vertical" gap={24} horizontalAlign="center" desktopHorizontalAlign="start" fullWidth>
              <Group direction="vertical" gap={12} horizontalAlign="center" desktopHorizontalAlign="start" fullWidth>
                <BodyTextBlock variant="large">
                  <FormattedMessage
                    id="IwKqqxqq1CZcQIZdc38t"
                    defaultMessage="Exclusive {luxPlusLogo} Member Event"
                    description="Eyebrow label shown above the main heading on the LuxPlus+ Month campaign landing page hero section"
                    values={{ luxPlusLogo: <LuxPlusLogo colour="inherit" height={12} inline /> }}
                  />
                </BodyTextBlock>
                <HeroHeading variant="heading2">
                  <FormattedMessage
                    id="lBr9l41F0kv3MnBj9Ej2"
                    defaultMessage="<i>Bigger</i> savings. Same unforgettable escapes"
                    description="Main heading on the LuxPlus+ Month campaign landing page hero section, promoting bigger savings on the same escapes"
                    values={{ i: renderItalic }}
                  />
                </HeroHeading>
              </Group>
              <Group direction="horizontal" gap={8} horizontalAlign="center" desktopHorizontalAlign="start" wrap="wrap">
                <AssistChip variant="outline" size="medium" onClick={scrollToHotels} startIcon={<LineBedIcon />}>
                  <FormattedMessage
                    id="puHURGXuYMOPywIUy-b-"
                    defaultMessage="Hotels"
                    description="Label for the hotels vertical chip on the LuxPlus+ Month campaign landing page hero section"
                  />
                </AssistChip>
                {enabledVerticals.tours && <AssistChip variant="outline" size="medium" onClick={scrollToTours} startIcon={<LineCompassIcon />}>
                  <FormattedMessage
                    id="NXEemopCN3h2NkBP7eCQ"
                    defaultMessage="Tours"
                    description="Label for the tours vertical chip on the LuxPlus+ Month campaign landing page hero section"
                  />
                </AssistChip>}
                {enabledVerticals.flights && <AssistChip variant="outline" size="medium" onClick={scrollToFlights} startIcon={<LinePlaneDepartureIcon />}>
                  <FormattedMessage
                    id="qIrUzTLj1sbSbBjGwKxA"
                    defaultMessage="Flights"
                    description="Label for the flights vertical chip on the LuxPlus+ Month campaign landing page hero section"
                  />
                </AssistChip>}
                {enabledVerticals.experiences && <AssistChip variant="outline" size="medium" onClick={scrollToExperiences} startIcon={<LineHotAirBalloonIcon />}>
                  <FormattedMessage
                    id="PBCCyaUiGWBBBw3ad-3d"
                    defaultMessage="Experiences"
                    description="Label for the experiences vertical chip on the LuxPlus+ Month campaign landing page hero section"
                  />
                </AssistChip>}
              </Group>
            </Group>
          </HeroTextContent>
        </HeroPanel>
        <HeroImagePanel>
          <HeroImageFrame>
            <ImageLoadDetector image={{ url: LUX_PLUS_MONTH_POOL_IMAGE_URL }} alt="" onLoad={onHeroImageLoad} />
            <HeroImageRing className={clsx({ visible: heroImageLoaded })} />
          </HeroImageFrame>
          <SavingsBadge>
            <BodyText variant="small" colour="inherit">
              <FormattedMessage
                id="Tp7iZ21gVCoJpZxoAcPL"
                defaultMessage="Members save"
                description="First line of the member savings badge shown over the LuxPlus+ Month campaign landing page hero image"
              />
            </BodyText>
            <SavingsAmountRow>
              <SavingsAmountPrefix>
                <SavingsOverText variant="small" colour="inherit">
                  <FormattedMessage
                    id="YU9HM34FIHiqTKaBAdIp"
                    defaultMessage="over"
                    description="Line preceding the dollar amount in the member savings badge, shown over the LuxPlus+ Month campaign landing page hero image"
                  />
                </SavingsOverText>
              </SavingsAmountPrefix>
              <SavingsAmount variant="heading3" colour="inherit" className={clsx({ 'long-amount': isLongSaveAmount })}>
                <FormatCurrency value={saveAmount} format="roundedDollar" />
              </SavingsAmount>
            </SavingsAmountRow>
            <BodyText variant="small" colour="inherit">
              <FormattedMessage
                id="cIBX1WP_EzmpL7otC3_e"
                defaultMessage="each year*"
                description="Suffix for annual savings amount with asterisk disclaimer"
              />
            </BodyText>
          </SavingsBadge>
        </HeroImagePanel>
      </Hero>
    </HeroContainer>
  )
}

export default LuxPlusMonthTakeoverHero
